import React, { useCallback, useMemo, useState, FC, ReactNode } from "react";

import { theme } from "@ovou-inc/ovou-ui";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";

import { Flex, SelectFormField, Box, Text, TextFormField } from "components";
import { useSettings } from "../queries/useSettings";
import {
  useCreateTeamTag,
  useDeleteTag,
  useGetTeamTags,
  useUpdateTeamTag,
} from "../queries/useTags";
import { IFormValues, ITag, TagAction, IMode } from "../types/Tag";
import { ACTION, VALIDATION_MESSAGES } from "../../../constants";
import { Button, useBoolean } from "@chakra-ui/react";
import ConfirmModal from "../../../components/ConfirmModal";
import { CONFIRM_MESSAGE } from "constants/Texts";

interface IAddOrEditTagProps {
  mode: IMode;
  closeTagItem: () => void;
  teamId: number;
  refetchContactsData: () => void;
}

interface ColorItem {
  color: string;
  name: string;
}

interface ColorOption {
  label: ReactNode;
  value: string;
}

const ColorLabel: FC<ColorItem> = ({ color, name }) => (
  <Flex alignItems="center">
    <Flex width="8px" height="8px" borderRadius="50%" backgroundColor={color} />
    <Text
      color={theme.palette.brand.primary.black}
      fontSize={14}
      fontWeight="700"
      marginLeft="5px"
    >
      {name}
    </Text>
  </Flex>
);

const AddOrEditTag: FC<IAddOrEditTagProps> = ({
  mode,
  teamId,
  refetchContactsData,
  closeTagItem,
}) => {
  const { data } = useSettings();

  const colorsList = data?.tag_colors || [];
  const { data: { results: tagsList = [] } = {}, refetch: getTeamTags } =
    useGetTeamTags({ teamId });

  const [isConfirm, { on: openConfirm, off: closeConfirm }] = useBoolean();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { mutate: fireCreateTag, isLoading: isTagCreating } = useCreateTeamTag({
    teamId,
    refetchTags: getTeamTags,
  });
  const { mutate: fireUpdateTag } = useUpdateTeamTag({
    tagId: mode.tag?.id || 0,
    teamId,
    refetchTags: getTeamTags,
  });
  const { mutate: fireDeleteTag, isLoading: isTagDeleting } = useDeleteTag({
    refetchTags: () => {
      getTeamTags();
      refetchContactsData();
    },
  });

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required(VALIDATION_MESSAGES.IS_REQUIRED("Tag Name")),
  });
  const colorsListOption = useMemo<ColorOption[]>(() => {
    return colorsList?.map((item: ColorItem) => ({
      label: <ColorLabel {...item} />,
      value: item.color,
    }));
  }, [colorsList?.tag_colors]);

  const filteredColorsListOption = useMemo<ColorOption[]>(() => {
    return colorsList
      ?.filter(
        (el: ColorItem) =>
          !tagsList.find((tag: ITag) => tag.color === el.color),
      )
      .map((item: ColorItem) => ({
        label: <ColorLabel {...item} />,
        value: item.color,
      }));
  }, [colorsList, tagsList]);

  const handleDeleteTag = (
    numContacts: number | undefined,
    setValues: FormikHelpers<any>["setValues"],
  ): void => {
    setValues((values: any) => ({
      ...values,
      action: ACTION.DELETE,
    }));
    if (numContacts && numContacts > 0) {
      openConfirm();
      return;
    } else {
      setConfirmDelete(true);
    }
  };

  const handleSubmit = useCallback(
    (values: IFormValues, formikHelpers: FormikHelpers<IFormValues>) => {
      const { setFieldError } = formikHelpers;
      if (!values.color) {
        setFieldError("color", VALIDATION_MESSAGES.IS_REQUIRED("Color select"));
        return;
      }
      switch (values.action) {
        case ACTION.ADD:
          fireCreateTag(
            { name: values.name, color: values.color },
            {
              onSuccess: () => {
                closeTagItem();
              },
            },
          );
          break;

        case ACTION.EDIT:
          fireUpdateTag(
            {
              tagId: values.id,
              name: values.name,
              color: values.color,
            },
            {
              onSuccess: () => {
                closeTagItem();
              },
            },
          );
          break;
        case ACTION.DELETE:
          if (confirmDelete) {
            fireDeleteTag(
              {
                tagId: values.id,
              },
              {
                onSuccess: () => {
                  closeTagItem();
                },
              },
            );
          }
          break;

        default:
          break;
      }
    },
    [fireCreateTag, confirmDelete, closeTagItem, fireDeleteTag],
  );
  const handleConfirm = (tagId: number): void => {
    closeConfirm();
    fireDeleteTag(
      {
        tagId,
      },
      {
        onSuccess: () => {
          closeTagItem();
        },
      },
    );
  };
  const handleCancelDelete = useCallback(
    (setValues: FormikHelpers<IFormValues>["setValues"]): void => {
      setValues((values: IFormValues) => ({
        ...values,
        action: ACTION.EDIT as TagAction,
      }));
      closeConfirm();
    },
    [closeConfirm],
  );
  return (
    <Formik
      initialValues={{
        name: mode.tag?.name || "",
        color: mode.tag?.color || "",
        id: mode.tag?.id || -1,
        num_contacts: mode.tag?.num_contacts || 0,
        action: mode.action,
      }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ values, isValid, setValues, dirty }) => {
        return (
          <Form>
            <Field label="Tag name" name="name" component={TextFormField} />

            <Box mt="32px">
              <Field
                instanceId="color"
                name="color"
                component={SelectFormField}
                label="Select Color"
                filterOptions={filteredColorsListOption}
                options={colorsListOption}
                maxMenuHeight={100}
                isSearchable={false}
              />
            </Box>

            <Box margin="32px 0">
              {mode.action === ACTION.EDIT ? (
                <Flex>
                  <Button
                    width="100%"
                    type="submit"
                    variant="outline"
                    _focus={{ outline: "none" }}
                    _hover={{ filter: "brightness(0.9)" }}
                    isLoading={isTagDeleting}
                    isDisabled={isTagDeleting || !isValid}
                    onClick={() =>
                      handleDeleteTag(values.num_contacts, setValues)
                    }
                    marginRight="8px"
                  >
                    Delete tag
                  </Button>
                  <Button
                    width="100%"
                    _focus={{ outline: "none" }}
                    _hover={{ filter: "brightness(0.9)" }}
                    type="submit"
                    // isLoading={isTagUpdating}
                    // isDisabled={isTagUpdating || !isValid}
                  >
                    Save changes
                  </Button>
                </Flex>
              ) : (
                <Button
                  width="100%"
                  _focus={{ outline: "none" }}
                  _hover={{ filter: "brightness(0.9)" }}
                  type="submit"
                  isLoading={isTagCreating}
                  isDisabled={
                    isTagCreating || !isValid || !dirty || !values.color
                  }
                >
                  Add Tag
                </Button>
              )}
            </Box>
            <ConfirmModal
              isOpen={isConfirm}
              onClose={() => handleCancelDelete(setValues)}
              onConfirm={() => handleConfirm(values.id)}
              isLoading={isTagDeleting}
              message={
                values.num_contacts && values.num_contacts > 1
                  ? CONFIRM_MESSAGE.DELETE_MULTI_USER
                  : CONFIRM_MESSAGE.DELETE_USER
              }
              modalContentStyle={{
                position: "fixed",
                right: "30px",
                top: "50px",
                width: "100%",
                transform: "translateX(0%) translateY(0px) translateZ(0px)",
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddOrEditTag;
